import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { adminService } from '../../../services/adminService';
import { extractList } from '../../../services/api';
import { parsePeriod } from '../../../constants';
import { Loading, EmptyState, Badge } from '../../../shared/components';
import './AdminCommonStyles.scss';

const MAX_UPLOAD_BYTES = 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1000;
const UPLOAD_TIMEOUT_MS = 30000;

const readImageElement = (file) => new Promise((resolve, reject) => {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(imageUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(imageUrl);
    reject(new Error('File gambar tidak bisa diproses'));
  };

  image.src = imageUrl;
});

const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
      return;
    }
    reject(new Error('Gagal membuat file foto terkompresi'));
  }, mimeType, quality);
});

const compressImageFile = async (file) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('File yang dipilih harus berupa gambar');
  }

  if (file.size <= MAX_UPLOAD_BYTES) {
    return file;
  }

  const image = await readImageElement(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.width, image.height));
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Browser tidak mendukung kompresi foto');
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const outputType = file.type === 'image/png' ? 'image/jpeg' : (file.type || 'image/jpeg');
  let quality = outputType === 'image/jpeg' ? 0.82 : 0.86;
  let blob = await canvasToBlob(canvas, outputType, quality);

  while (blob.size > MAX_UPLOAD_BYTES && quality > 0.45) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, outputType, quality);
  }

  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error('Ukuran foto masih melebihi batas 1 MB setelah dikompres');
  }

  return new File([blob], file.name.replace(/\.[^.]+$/, outputType === 'image/jpeg' ? '.jpg' : '.webp'), {
    type: outputType,
    lastModified: Date.now(),
  });
};

const buildUploadErrorMessage = ({ error, responseStatus, responseData }) => {
  if (error?.name === 'AbortError') {
    return 'Upload foto melebihi batas waktu 30 detik. Coba lagi dengan koneksi lebih stabil.';
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'Koneksi internet terputus. Periksa jaringan lalu coba lagi.';
  }

  if (typeof responseStatus === 'number' && responseStatus >= 500) {
    return 'Server upload sedang bermasalah. Coba beberapa saat lagi.';
  }

  if (typeof responseStatus === 'number' && responseStatus >= 400) {
    return 'Upload foto ditolak server: ' + (responseData?.message || responseData?.error || 'Periksa kembali file yang dipilih.');
  }

  if (responseData && responseData.status === false) {
    return 'Upload foto gagal: ' + (responseData.message || responseData.error || 'Terjadi kesalahan saat memproses file.');
  }

  if (error instanceof TypeError) {
    return 'Gagal terhubung ke server upload. Coba lagi beberapa saat lagi.';
  }

  return 'Upload foto gagal: ' + (error?.message || 'Terjadi kesalahan');
};

const UniformReportDetailPage = () => {
  const { uniformReportId } = useParams();
  const [report, setReport] = useState(null);
  const [uniforms, setUniforms] = useState([]);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showDetailForm, setShowDetailForm] = useState(false);
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [detailFormData, setDetailFormData] = useState({
    uniform_id: '',
    qty: '',
    url_photo: '',
    notes: '',
  });
  const photoInputRef = useRef(null);
  const retryFileRef = useRef(null);

  const canRetryUpload = Boolean(retryFileRef.current && uploadError && !uploading);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const resetUploadUiState = () => {
    setUploading(false);
    setUploadStage('');
    setUploadError('');
    setUploadSuccess('');
    retryFileRef.current = null;
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rRes, dRes, uRes] = await Promise.all([
        adminService.getReportById(uniformReportId),
        adminService.getReportDetails(uniformReportId, { page: 1, limit: 100 }),
        adminService.getUniforms({ limit: 100 }),
      ]);
      setReport(rRes.data);
      setDetails(extractList(dRes.data));
      setUniforms(extractList(uRes.data));
    } catch (err) {
      showToast(err?.message || 'Gagal memuat data laporan');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    const action = newStatus === 'approved' ? 'setujui' : 'tolak';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} laporan ini?`)) return;
    setUpdating(true);
    try {
      const res = await adminService.updateReport(uniformReportId, { report_status: newStatus });
      if (res.status) {
        setReport((prev) => ({ ...prev, report_status: newStatus }));
        showToast(`Laporan berhasil di${action}`);
      } else {
        showToast(res.message || 'Gagal memperbarui status laporan');
      }
    } catch (err) {
      showToast(err?.message || 'Gagal memperbarui laporan');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddDetail = () => {
    setEditingDetailId(null);
    setDetailFormData({ uniform_id: '', qty: '', url_photo: '', notes: '' });
    resetUploadUiState();
    setShowDetailForm(true);
  };

  const handleEditDetail = (detail) => {
    setDetailFormData({
      uniform_id: detail.uniform_id,
      qty: detail.qty,
      url_photo: detail.url_photo ?? '',
      notes: detail.notes ?? '',
    });
    resetUploadUiState();
    setEditingDetailId(detail.uniform_report_detail_id);
    setShowDetailForm(true);
  };

  const uploadPhoto = async (file) => {
    if (!file || uploading) return;

    if (!process.env.REACT_APP_CDN_URL || !process.env.REACT_APP_CDN_EMAIL || !process.env.REACT_APP_CDN_PASSWORD) {
      setUploadError('Konfigurasi CDN belum lengkap. Periksa REACT_APP_CDN_URL, REACT_APP_CDN_EMAIL, dan REACT_APP_CDN_PASSWORD.');
      return;
    }

    retryFileRef.current = file;
    setUploadError('');
    setUploadSuccess('');
    setUploading(true);
    setUploadStage('Menyiapkan foto...');

    let timeoutId;
    let responseStatus;
    let responseData;

    try {
      setUploadStage('Mengompres foto...');
      const uploadFile = await compressImageFile(file);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('email', process.env.REACT_APP_CDN_EMAIL);
      formData.append('password', process.env.REACT_APP_CDN_PASSWORD);
      formData.append('user_email', process.env.REACT_APP_CDN_EMAIL);

      const controller = new AbortController();
      timeoutId = window.setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

      setUploadStage('Mengupload foto...');
      const res = await fetch(process.env.REACT_APP_CDN_URL, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      responseStatus = res.status;
      responseData = await res.json();

      if (!res.ok) {
        throw new Error('Upload ditolak server');
      }

      if (responseData.status && responseData.url) {
        retryFileRef.current = null;
        setUploadError('');
        setUploadSuccess('Foto berhasil diupload dan siap disimpan.');
        setDetailFormData((prev) => ({ ...prev, url_photo: responseData.url }));
      } else {
        setUploadError(buildUploadErrorMessage({ responseStatus, responseData }));
      }
    } catch (err) {
      setUploadError(buildUploadErrorMessage({ error: err, responseStatus, responseData }));
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (photoInputRef.current) photoInputRef.current.value = '';
      setUploading(false);
      setUploadStage('');
    }
  };

  const handlePhotoChange = async (event) => {
    if (uploading) {
      event.target.value = '';
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';
    await uploadPhoto(file);
  };

  const handleRetryUpload = async () => {
    if (!retryFileRef.current || uploading) return;
    await uploadPhoto(retryFileRef.current);
  };

  const handleRemovePhoto = () => {
    setUploadError('');
    setUploadSuccess('');
    setDetailFormData((prev) => ({ ...prev, url_photo: '' }));
    retryFileRef.current = null;
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleCloseDetailForm = () => {
    if (uploading) return;
    resetUploadUiState();
    setShowDetailForm(false);
  };

  const handleSubmitDetail = async (e) => {
    e.preventDefault();

    if (uploading) {
      showToast('Tunggu proses upload foto selesai');
      return;
    }

    if (!detailFormData.uniform_id || !detailFormData.qty) {
      showToast('Uniform dan qty harus diisi');
      return;
    }

    const payload = {
      ...detailFormData,
      uniform_id: Number(detailFormData.uniform_id),
      qty: Number(detailFormData.qty),
      url_photo: detailFormData.url_photo?.trim() || null,
      notes: detailFormData.notes?.trim() || null,
    };

    if (!Number.isFinite(payload.uniform_id) || !Number.isFinite(payload.qty) || payload.qty <= 0) {
      showToast('Uniform dan qty harus valid (qty minimal 1)');
      return;
    }

    try {
      let res;
      if (editingDetailId) {
        res = await adminService.updateReportDetail(editingDetailId, payload);
      } else {
        res = await adminService.addReportDetail(uniformReportId, payload);
      }
      
      if (res.status) {
        showToast(res.message || 'Detail laporan berhasil disimpan');
        fetchData();
        resetUploadUiState();
        setShowDetailForm(false);
      } else {
        showToast(res.message || 'Gagal menyimpan detail laporan');
      }
    } catch (err) {
      showToast(err?.message || 'Gagal menyimpan detail laporan');
    }
  };

  const handleDeleteDetail = async (detailId) => {
    if (!window.confirm('Hapus detail ini?')) return;
    try {
      const res = await adminService.deleteReportDetail(detailId);
      if (res.status) {
        showToast(res.message || 'Detail berhasil dihapus');
        fetchData();
      } else {
        showToast(res.message || 'Gagal menghapus detail');
      }
    } catch (err) {
      showToast(err?.message || 'Gagal menghapus detail');
    }
  };

  if (loading) return <Loading />;
  if (!report) return <EmptyState message="Laporan tidak ditemukan" />;

  const { label } = parsePeriod(report.period);

  return (
    <div className="page-container">


      {toast && <div className="toast">{toast}</div>}

      <div className="detail-container">
        <div className="detail-header">
          <div>
            <h1>{label}</h1>
            <p>Employee: <strong>{report.user?.name || '—'}</strong> | Total: <strong>{details.reduce((s, d) => s + (Number(d.qty) || 0), 0)} pcs</strong></p>
            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {report.submitted_at ? `Submitted: ${report.submitted_at}` : 'Belum disubmit'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Badge type={report.report_status === 'approved' ? 'success' : report.report_status === 'rejected' ? 'danger' : 'warning'}>
              {report.report_status.toUpperCase()}
            </Badge>
          </div>
        </div>

        {report.report_status === 'submitted' && (
          <div className="detail-card" style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn-primary"
                onClick={() => handleUpdateStatus('approved')}
                disabled={updating}
              >
                ✅ Setujui Laporan
              </button>
              <button
                className="btn-danger"
                onClick={() => handleUpdateStatus('rejected')}
                disabled={updating}
              >
                ❌ Tolak Laporan
              </button>
            </div>
          </div>
        )}

        <div className="detail-card">
          <h3>Detail Item Seragam</h3>
          <button className="btn-primary" style={{ marginBottom: '1rem' }} onClick={handleAddDetail}>
            + Tambah Detail
          </button>

          {details.length === 0 ? (
            <EmptyState message="Tidak ada detail item" />
          ) : (
            <>
              <div className="table-card">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Seragam</th>
                      <th>Qty</th>
                      <th>Foto</th>
                      <th>Catatan</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((d) => (
                      <tr key={d.uniform_report_detail_id}>
                        <td><strong>{d.uniform?.name || `ID: ${d.uniform_id}`}</strong></td>
                        <td>{d.qty} pcs</td>
                        <td>
                          {d.url_photo ? (
                            <a href={d.url_photo} target="_blank" rel="noreferrer">
                              <img
                                src={d.url_photo}
                                alt="foto seragam"
                                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0', cursor: 'pointer' }}
                              />
                            </a>
                          ) : '—'}
                        </td>
                        <td>{d.notes || '—'}</td>
                        <td>
                          <button 
                            className="btn-sm btn-info"
                            onClick={() => handleEditDetail(d)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-sm btn-danger"
                            onClick={() => handleDeleteDetail(d.uniform_report_detail_id)}
                          >
                            Hapus
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>


            </>
          )}
        </div>
      </div>

      {showDetailForm && (
        <div className="modal-overlay" onClick={handleCloseDetailForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingDetailId ? 'Edit Detail' : 'Tambah Detail Baru'}</h2>
            <form onSubmit={handleSubmitDetail}>
              <div className="form-group">
                <label>Seragam *</label>
                <select
                  value={detailFormData.uniform_id}
                  onChange={(e) => setDetailFormData({ ...detailFormData, uniform_id: e.target.value })}
                  required
                >
                  <option value="">Pilih seragam</option>
                  {uniforms.map((u) => (
                    <option key={u.uniform_id} value={u.uniform_id}>
                      {u.code} - {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Qty *</label>
                <input
                  type="number"
                  min="1"
                  value={detailFormData.qty}
                  onChange={(e) => setDetailFormData({ ...detailFormData, qty: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Foto</label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={handlePhotoChange}
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.4rem' }}>
                  Format JPG/JPEG/PNG. Maks 1 MB setelah kompresi.
                </small>
                {uploading && (
                  <small style={{ color: '#555', display: 'block', marginTop: '0.4rem' }}>
                    {uploadStage || 'Mengupload foto...'}
                  </small>
                )}
                {uploadSuccess && !uploadError && !uploading && (
                  <small style={{ color: '#2e7d32', display: 'block', marginTop: '0.4rem' }}>
                    {uploadSuccess}
                  </small>
                )}
                {uploadError && (
                  <small style={{ color: '#c62828', display: 'block', marginTop: '0.4rem' }}>
                    {uploadError}
                  </small>
                )}
                {canRetryUpload && (
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginTop: '0.6rem' }}
                    onClick={handleRetryUpload}
                    disabled={uploading}
                  >
                    Coba Upload Lagi
                  </button>
                )}
                {detailFormData.url_photo && (
                  <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <a href={detailFormData.url_photo} target="_blank" rel="noreferrer">
                      <img
                        src={detailFormData.url_photo}
                        alt="Preview foto"
                        style={{
                          width: 84,
                          height: 84,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                        }}
                      />
                    </a>
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={handleRemovePhoto}
                      disabled={uploading}
                    >
                      Hapus Foto
                    </button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Catatan</label>
                <textarea
                  value={detailFormData.notes ?? ''}
                  onChange={(e) => setDetailFormData({ ...detailFormData, notes: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={uploading}>
                  {uploading ? 'Mengupload Foto...' : 'Simpan'}
                </button>
                <button type="button" className="btn-secondary" onClick={handleCloseDetailForm} disabled={uploading}>
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniformReportDetailPage;
